import Close16 from '@carbon/icons-react/es/close/16';
import { Divider, FormInstance, Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import moment, { MomentInput } from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { ProjectPhase, ShiftDatesType } from 'api/project';
import { Button } from 'components/atoms/button';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
} from 'components/atoms/drawer';
import { Form } from 'components/atoms/form';
import { Radio } from 'components/atoms/radio';
import { Select } from 'components/atoms/select';
import { Switch } from 'components/atoms/switch/switch';
import { Help } from 'components/organisms/task-pane/task-detail/task-fields/styled';
import { ShiftDatesForm } from 'components/organisms/tasks/bulk-editor/shift-dates/form';
import {
  Direction,
  ShiftBy,
} from 'components/organisms/tasks/bulk-editor/shift-dates/model';
import {
  ButtonGroup,
  SubTitle,
} from 'components/organisms/tasks/duplicate-task/style';
import { updateGanttDatesAction } from 'data/projects/actions';
import { useActiveBaselinesByProjectId } from 'data/projects/hooks/use-project-baseline';
import { useProjectPhaseByProjectId } from 'data/projects/hooks/use-project-phase.store';
import { updateProjectPhases } from 'data/projects/sagas';
import { Task } from 'data/tasks';
import { useTasksByProjectId } from 'data/tasks/hooks';
import { updateTasksDates } from 'data/tasks/slice';
import { useGetAlignPhaseDatesSetting } from 'queries/advanced-settings/advanced-settings';
import { useBulkShiftDatesMutation } from 'queries/project';
import { getProjectBaselineQueryKey } from 'queries/project-baseline-mutation';
import {
  addDays,
  Entity,
  EntityDateShiftUpdate,
  EntityType,
  getDateShiftsForEntity,
  isWeekEnd,
} from 'utils/date-shift';
import { BaselineForm } from '../baseline/form';
import { useBaselineRequestPayload } from '../baseline/use-request-payload';
import { formatDate, getPhaseList, getTaskList } from '../helpers';

interface Props {
  onClose: () => void;
  projectId: number;
  ganttRef: any;
  setBaseline: (index?: number) => void;
}

interface FormProps {
  form: FormInstance<any>;
  shiftDaysForm: FormInstance<any>;
  baselineForm: FormInstance<any>;
  projectId: number;
}

const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;

const InlineFormItem = styled(Form.Item)`
  flex-direction: row-reverse !important;
  align-items: center;
  gap: var(--spacing-03);
  justify-content: flex-end;

  .ant-form-item-control {
    flex: none;
  }

  .ant-form-item-label,
  .ant-form-item-label > label {
    font-size: var(--font-size-base) !important;
  }
`;

const newDateAfterShift = (date, duration, unit, direction) => {
  if (unit === ShiftBy.Days) {
    return formatDate(addDays(date, duration * direction));
  }
  let _date = moment(date).add(
    duration * direction,
    unit === ShiftBy.Weeks ? 'week' : 'month',
  );

  if (isWeekEnd(_date)) {
    _date = moment(_date).add(_date.day() === 6 ? 2 : 1, 'day');
  }

  return formatDate(_date);
};

const ShiftGanttForm = ({
  form,
  projectId,
  shiftDaysForm,
  baselineForm,
}: FormProps) => {
  const activeBaselines = useActiveBaselinesByProjectId(projectId);
  const tasks = useTasksByProjectId(projectId);

  /**
   * Those tasks which
   * 1. belongs to a phase and
   * 2. has a start date
   * will be listed in the dropdown.
   */
  const taskOptions = useMemo(() => {
    return tasks
      .filter(
        (_task) => !!_task.projectPhase?.projectPhaseId && _task.startDate,
      )
      .map((_task) => ({
        label: _task.taskName,
        value: _task.taskId,
      }));
  }, [tasks]);

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        shiftType: ShiftDatesType.PROJECTPLAN,
      }}
    >
      <Form.Item name="shiftType">
        <StyledRadioGroup>
          <Radio value={ShiftDatesType.PROJECTPLAN}>
            <FormattedMessage id="timeline.shift.dates.form.radio.option.one" />
          </Radio>
          <Radio
            value={ShiftDatesType.TASKS}
            disabled={taskOptions.length <= 0}
          >
            <FormattedMessage id="timeline.shift.dates.form.radio.option.two" />
          </Radio>
        </StyledRadioGroup>
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.shiftType !== currentValues.shiftType
        }
      >
        {({ getFieldValue }) =>
          getFieldValue('shiftType') === ShiftDatesType.TASKS ? (
            <Form.Item name="taskId" required>
              <Select
                options={taskOptions}
                placeholder={
                  <FormattedMessage id="timeline.shift.dates.select.task.placeholder" />
                }
              />
            </Form.Item>
          ) : null
        }
      </Form.Item>
      <ShiftDatesForm form={shiftDaysForm} />
      <Divider />
      <InlineFormItem
        name="addBaseline"
        valuePropName="checked"
        label={
          <>
            <FormattedMessage id="timeline.shift.dates.baseline.switch.label" />
            <Tooltip
              title={
                <FormattedMessage id="timeline.shift.dates.baseline.switch.label.tooltip" />
              }
            >
              <Help />
            </Tooltip>
          </>
        }
      >
        <Switch />
      </InlineFormItem>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.addBaseline !== currentValues.addBaseline
        }
      >
        {({ getFieldValue }) =>
          getFieldValue('addBaseline') ? (
            <BaselineForm form={baselineForm} baselines={activeBaselines} />
          ) : null
        }
      </Form.Item>
    </Form>
  );
};

const getPhaseDates = (
  phase: ProjectPhase,
  taskList: { [id: number]: Entity },
  taskUpdates: { [id: number]: EntityDateShiftUpdate },
  alignPhaseDates: boolean,
) => {
  const _default = [phase.startDate, phase.dueDate];

  return phase.taskOrder
    ? phase.taskOrder?.reduce(
        (prev: MomentInput[], taskId: number): MomentInput[] => {
          const taskStartDate =
            taskUpdates[taskId]?.startDate ?? taskList[taskId].startDate;

          const taskDueDate =
            taskUpdates[taskId]?.dueDate ?? taskList[taskId].dueDate;

          return [
            !prev[0]
              ? taskStartDate || ''
              : taskStartDate && moment(taskStartDate).isBefore(moment(prev[0]))
              ? formatDate(moment(taskStartDate))
              : prev[0],

            !prev[1]
              ? taskDueDate || ''
              : taskDueDate && moment(taskDueDate).isAfter(moment(prev[1]))
              ? formatDate(moment(taskDueDate))
              : prev[1],
          ];
        },
        alignPhaseDates ? [] : _default,
      )
    : _default;
};

export const ShiftDatesDrawer = ({
  onClose,
  projectId,
  ganttRef,
  setBaseline,
}: Props) => {
  const [form] = Form.useForm();
  const [shiftDaysForm] = Form.useForm();
  const [baselineForm] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { getCreateRequestPayload } = useBaselineRequestPayload({ ganttRef });
  const alignPhaseDates = useGetAlignPhaseDatesSetting();

  const projectPhases = useProjectPhaseByProjectId(projectId);
  const tasks = useTasksByProjectId(projectId);

  const phaseList = useMemo(() => getPhaseList(projectPhases), [projectPhases]);
  const taskList = useMemo(
    () => getTaskList(tasks, phaseList),
    [phaseList, tasks],
  );

  const getAllSubTaskIds = useCallback(
    (_tId): number[] => {
      const _task = taskList[_tId];

      return _task.children
        ? [
            ..._task.children,
            ..._task.children.flatMap((_cId) => getAllSubTaskIds(_cId)),
          ]
        : [];
    },
    [taskList],
  );

  const { mutateAsync: bulkShiftDates } = useBulkShiftDatesMutation();

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const _onClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => requestAnimationFrame(onClose), 300);
  }, [onClose]);

  const onSubmit = useCallback(async () => {
    const formValues = await form.validateFields();
    const shiftDaysFormValues = await shiftDaysForm.validateFields();
    const baselineFormValues = await baselineForm.validateFields();
    let baselinePayload;

    if (!isEmpty(baselineFormValues)) {
      baselinePayload = getCreateRequestPayload(baselineFormValues);
      setBaseline();
    }

    const { shiftType, taskId } = formValues;
    const taskIdValue = taskId ? taskId.value : null;
    const { duration, unit, direction } = shiftDaysFormValues;
    const directionVector = direction === Direction.Earlier ? -1 : 1;
    let refPhaseIndex = 0;
    let refTaskIndex = 0;
    let refTask: Task | undefined;
    let refPhase: ProjectPhase | undefined;

    if (taskIdValue) {
      refTask = tasks.find((_task) => _task.taskId === taskIdValue);

      const phaseId = refTask?.projectPhase?.projectPhaseId;

      projectPhases.every((_phase, index) => {
        if (_phase.projectPhaseId === phaseId) {
          refPhase = _phase;
          refPhaseIndex = index;

          return false;
        }

        return true;
      });

      refPhaseIndex = projectPhases.findIndex(
        (_phase) => _phase.projectPhaseId === phaseId,
      );

      refTaskIndex =
        refPhase?.taskOrder?.findIndex(
          (_taskId) => _taskId === refTask?.taskId,
        ) ?? 0;
    }

    const _phases = projectPhases.slice(refPhaseIndex);
    const _tasks = projectPhases.slice(refPhaseIndex).reduce((prev, _phase) => {
      const taskIds = _phase.taskOrder?.slice(
        _phase.projectPhaseId === refPhase?.projectPhaseId ? refTaskIndex : 0,
      );
      const filteredTasks = taskIds
        ?.filter(
          (_t) =>
            (taskList[_t].hasStartDate || taskList[_t].hasDueDate) &&
            (!taskIdValue ||
              moment(taskList[_t].startDate).isSameOrAfter(
                moment(refTask?.startDate),
              )),
        )
        .flatMap((_tId) => {
          const subTaskIds = getAllSubTaskIds(_tId);

          return [_tId, ...subTaskIds];
        })
        .map((_tId) => taskList[_tId])
        .filter(Boolean);

      return filteredTasks ? [...prev, ...filteredTasks] : prev;
    }, [] as Entity[]);

    const _taskUpdates: EntityDateShiftUpdate[] = _tasks.map((_task) => ({
      id: _task.id,
      startDate: newDateAfterShift(
        _task.startDate,
        duration,
        unit,
        directionVector,
      ),
      dueDate: newDateAfterShift(
        _task.dueDate,
        duration,
        unit,
        directionVector,
      ),
    }));

    const taskUpdates = _taskUpdates.reduce(
      (prev, current) => ({
        ...prev,
        [current.id]: current,
      }),
      {},
    );

    const _phaseUpdates: EntityDateShiftUpdate[] = _phases.map((_phase) => {
      const [newStartDate, newDueDate] = getPhaseDates(
        _phase,
        taskList,
        taskUpdates,
        alignPhaseDates,
      );

      return {
        id: _phase.projectPhaseId,
        startDate: newStartDate,
        dueDate: newDueDate,
      };
    });

    const phaseUpdates = _phaseUpdates.reduce(
      (prev, current) => ({
        ...prev,
        [current.id]: current,
      }),
      {},
    );

    const taskListWithUpdates = Object.values(taskList).reduce(
      (prev, _task) => ({
        ...prev,
        [_task.id]:
          _task.id === refTask?.taskId
            ? _task
            : { ..._task, ...taskUpdates[_task.id] },
      }),
      {},
    );

    // const phaseListWithUpdates = Object.values(phaseList).reduce(
    //   (prev, _phase) => ({
    //     ...prev,
    //     [_phase.id]: { ..._phase, ...phaseUpdates[_phase.id] },
    //   }),
    //   {},
    // );

    const [autoPhaseUpdates, autoTaskUpdates] = !taskIdValue
      ? [_phaseUpdates, _taskUpdates]
      : getDateShiftsForEntity(
          phaseList,
          taskListWithUpdates,
          {
            id: taskIdValue,
            type: EntityType.Task,
            depth: taskListWithUpdates[taskIdValue].depth,
            startDate: newDateAfterShift(
              refTask?.startDate,
              duration,
              unit,
              directionVector,
            ),
            dueDate: newDateAfterShift(
              refTask?.dueDate,
              duration,
              unit,
              directionVector,
            ),
          },
          {
            allowWeekend: false,
            alignPhaseWithTask: alignPhaseDates,
          },
        );

    const finalPhaseUpdates = {
      ...phaseUpdates,
      ...Object.values(autoPhaseUpdates).reduce(
        (prev, _phaseUpdate) => ({
          ...prev,
          [_phaseUpdate.id]: {
            ...(phaseUpdates[_phaseUpdate.id] || {}),
            ..._phaseUpdate,
          },
        }),
        {},
      ),
    };

    const finalTaskUpdates = {
      ...taskUpdates,
      ...Object.values(autoTaskUpdates).reduce(
        (prev, _taskUpdate) => ({
          ...prev,
          [_taskUpdate.id]: {
            ...(taskUpdates[_taskUpdate.id] || {}),
            ..._taskUpdate,
          },
        }),
        {},
      ),
    };

    dispatch(
      updateGanttDatesAction({
        phaseUpdates: finalPhaseUpdates,
        taskUpdates: finalTaskUpdates,
        phaseList,
        taskList,
      }),
    );
    dispatch(updateTasksDates(Object.values(finalTaskUpdates)));
    updateProjectPhases(
      projectId,
      Object.values(finalPhaseUpdates),
      queryClient,
    );
    _onClose();

    await bulkShiftDates({
      projectId,
      payload: {
        shiftType,
        shiftValue: duration,
        shiftUnit: unit.toUpperCase(),
        shiftCondition: direction.toUpperCase(),
        ...(taskIdValue && refTask?.startDate && refPhase
          ? {
              taskId: taskIdValue,
              taskStartDate: refTask.startDate,
              phaseId: refPhase.projectPhaseId,
              phaseStartDate: refPhase.startDate,
            }
          : {}),
        ...(!isEmpty(baselinePayload)
          ? {
              projectBaseline: baselinePayload,
            }
          : {}),
      },
    });
    if (!isEmpty(baselinePayload)) {
      queryClient.refetchQueries(getProjectBaselineQueryKey(projectId));
    }
  }, [
    _onClose,
    alignPhaseDates,
    baselineForm,
    bulkShiftDates,
    dispatch,
    form,
    getAllSubTaskIds,
    getCreateRequestPayload,
    phaseList,
    projectId,
    projectPhases,
    queryClient,
    setBaseline,
    shiftDaysForm,
    taskList,
    tasks,
  ]);

  return (
    <CustomLayoutDrawer
      width={370}
      placement="right"
      closeIcon={<Close16 />}
      visible={visible}
      mask
      destroyOnClose
      onClose={_onClose}
      closable
    >
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title>
          <FormattedMessage id="timeline.shift_dates.button.text" />
          <SubTitle>
            <FormattedMessage id="settings" />
          </SubTitle>
        </CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Content>
          <ShiftGanttForm
            form={form}
            baselineForm={baselineForm}
            shiftDaysForm={shiftDaysForm}
            projectId={projectId}
          />
        </CustomLayoutDrawerContent.Content>
        <CustomLayoutDrawerContent.Footer>
          <ButtonGroup>
            <Button onClick={_onClose}>
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              // loading={isDuplicateInProgress}
              type="primary"
              onClick={onSubmit}
              // disabled={buttonDisabled}
            >
              <FormattedMessage id="confirm" />
            </Button>
          </ButtonGroup>
        </CustomLayoutDrawerContent.Footer>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
